// The signed-in user, loaded once and shared — and the three screens that stand in
// for the app when there is no usable identity behind it.
//
// GET /api/me is the app shell's first call (docs/API.md §2): it returns the user,
// both capability flags, the CSRF token, whether this is the dev stub identity, and
// `products` — the entitlements this account holds.
//
// Capability flags are for *rendering*, never for authorization — the server checks
// every one of them again, and so does the database (SPEC.md §3.3). That applies to
// the access-denied screen below too: it is what a revoked agent SEES, not what stops
// them. What stops them is `requireProduct` on every gated route, which re-reads
// `user_product_roles` on every request (src/middleware/auth.js).

import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { api, onCsrfInvalid, setCsrfToken, signOut as postSignOut } from '../../src/api/client.js';
import { productRole, atLeastRole } from './roles.js';
import { identityChanged } from './identity.js';

const SessionContext = createContext(null);
const SignOutContext = createContext(null);
const IdentityCheckContext = createContext(null);
const SessionEndedContext = createContext(null);

export function SessionProvider({ children }) {
  const [state, setState] = useState({ status: 'loading', user: null, error: null });

  useEffect(() => {
    let cancelled = false;

    api.get('/me')
      .then((user) => {
        if (cancelled) return;
        setCsrfToken(user.csrf_token);
        setState({ status: 'ready', user, error: null });
      })
      .catch((error) => {
        if (cancelled) return;
        // A 401 has ALREADY called `window.location.assign('/auth/login')` inside the
        // client (`goToLogin`), so this component is about to be torn down by a
        // navigation — but `assign` does not stop React from rendering first, and the
        // error screen used to flash "Can't reach the support desk / Sign in to
        // continue." on every expired session on the way out. Stay on the loading
        // screen instead and say what is happening: the redirect is in flight, not a
        // failure.
        if (error?.status === 401) {
          setState({ status: 'redirecting', user: null, error: null });
          return;
        }
        // Anything else is a real failure worth showing rather than a blank screen.
        setState({ status: 'error', user: null, error });
      });

    return () => { cancelled = true; };
  }, []);

  // WHO IS SIGNED IN NOW, re-read: GET /api/me against the id this tab was loaded as
  // (identity.js). A different person reloads the page clean, so this tab never shows or
  // saves the previous person's draft; the same person takes the fresh CSRF token. Resolves
  // `true` only for a confirmed same person. A GET /api/me that fails resolves `false` and
  // changes nothing (a 401 has already sent the browser to sign in, inside the client).
  // Checks that overlap share one request.
  const userId = state.user?.id ?? null;
  const identityCheck = useRef(null);
  const checkIdentity = useCallback(() => {
    if (identityCheck.current) return identityCheck.current;
    const check = api.get('/me')
      .then((me) => {
        if (identityChanged(userId, me)) {
          window.location.reload();
          return false;
        }
        setCsrfToken(me.csrf_token);
        return true;
      }, () => false)
      .finally(() => { identityCheck.current = null; });
    identityCheck.current = check;
    return check;
  }, [userId]);

  // Asked by the client on `403 CSRF_TOKEN_INVALID` (../api/client.js): `true` is "same
  // person, fresh token taken", and the refused request is sent again once. Registered
  // only once there is a user to compare with.
  useEffect(() => {
    if (userId == null) return undefined;
    return onCsrfInvalid(checkIdentity);
  }, [userId, checkIdentity]);

  /**
   * Ends the session and stays put.
   *
   * DELIBERATELY NOT FOLLOWED BY A RELOAD. Every entry into this app calls GET
   * /api/me, which would now be 401, which sends the browser straight back to
   * /auth/login — and Entra, whose own SSO session this cannot end (see
   * `signOut` in ../api/client.js), would sign the same person back in without
   * asking. A sign-out button that visibly signs you back in is worse than none, so
   * the last thing this tab does is say so and stop.
   */
  // Set BEFORE the signed-out screen replaces the app, so a screen unmounting on the way
  // out (the create form, which flushes its draft on unmount) can tell it must not send.
  const ended = useRef(false);
  const sessionEnded = useCallback(() => ended.current, []);

  const signOut = useCallback(async () => {
    await postSignOut();
    ended.current = true;
    setCsrfToken(null);
    setState({ status: 'signed-out', user: null, error: null });
  }, []);

  if (state.status === 'loading' || state.status === 'redirecting') {
    return (
      // These screens render before AppLayout exists, so they sit directly on
      // index.html's bg-canvas — which the boot script has already resolved to the
      // right theme, so there is nothing to flash here either.
      <div className="grid min-h-screen place-items-center text-sm text-ink-muted">
        {state.status === 'redirecting' ? 'Taking you to sign in…' : 'Loading…'}
      </div>
    );
  }

  if (state.status === 'signed-out') {
    return (
      // WHAT THIS SCREEN MAY CLAIM, and why it no longer claims more.
      //
      // "Your session on this computer has ended" is what it used to say, and an
      // agent reads that as "the next person cannot get into my account" — while
      // the screen itself supplies the button that disproves it. Signing out
      // destroys our session row and the crm_session cookie; it cannot touch
      // Entra's own SSO cookie on this machine (../api/client.js, and
      // docs/superpowers/plans/2026-08-20-entra-sso-followups.md §5), and that
      // cookie is what actually decides who gets back in.
      //
      // What changed underneath: src/auth/oidc.js now sends
      // `prompt=select_account`, so the way back in is no longer SILENT — Entra
      // asks which account, by name, on the button below and equally on a reload,
      // the Back button, or typing `/`. What it is NOT is closed: the departed
      // agent's tile is on that list and one deliberate click still signs the next
      // person in as them, with no password. Only `prompt=login` or genuinely
      // ending the Entra session would close it, and neither is done.
      //
      // So the text below says exactly that much: the desk is clear, the browser is
      // still signed in to Microsoft, and closing it is what hands over cleanly.
      // Anything shorter is the false reassurance this replaced.
      <div className="grid min-h-screen place-items-center px-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold">Signed out</h1>
          <p className="mt-2 text-sm text-ink-secondary">
            Your Adora Ops session on this computer has ended.
          </p>
          <p className="mt-2 text-sm text-ink-secondary">
            This browser is still signed in to Microsoft. Signing in again will ask which
            account to use, and yours will be on the list, so if you’re handing this desk
            to someone else, close the browser first.
          </p>
          {/* A plain link, not a router link: /auth/login is the server's, and a
              client-side navigation would never leave the SPA. */}
          <a className="btn-primary mt-4 inline-block" href="/auth/login">Sign in</a>
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="grid min-h-screen place-items-center px-6">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold">Can’t reach Adora Ops</h1>
          <p className="mt-2 text-sm text-ink-secondary">{state.error?.message}</p>
          <button type="button" className="btn-primary mt-4" onClick={() => window.location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  // An identity entitled to nothing (`products: []`) is NOT decided here any more. The
  // shell decides, because "nothing available" now also covers roles held only in
  // modules this server has not switched on, and it renders the no-access page with
  // the sign-out control (web/shell/NoAccess.jsx). What a revoked agent SEES moved;
  // what STOPS them did not: requireProduct still refuses every gated call.
  return (
    <SignOutContext.Provider value={signOut}>
      <SessionContext.Provider value={state.user}>
        <IdentityCheckContext.Provider value={checkIdentity}>
          <SessionEndedContext.Provider value={sessionEnded}>
            {children}
          </SessionEndedContext.Provider>
        </IdentityCheckContext.Provider>
      </SessionContext.Provider>
    </SignOutContext.Provider>
  );
}

/**
 * A fixed, signed-in session with no /api/me read: for rendering the shell's chrome in tests
 * (test/web/shell-layout.test.js). The app never mounts it, so the build drops it.
 */
export function StaticSession({ user, signOut, children }) {
  return (
    <SignOutContext.Provider value={signOut}>
      <SessionContext.Provider value={user}>{children}</SessionContext.Provider>
    </SignOutContext.Provider>
  );
}

/** The current user. Never null inside the provider. */
export function useSession() {
  const user = useContext(SessionContext);
  if (!user) throw new Error('useSession must be used inside <SessionProvider>');
  return user;
}

/** Ends the session on this computer. Never null inside the provider. */
export function useSignOut() {
  const signOut = useContext(SignOutContext);
  if (!signOut) throw new Error('useSignOut must be used inside <SessionProvider>');
  return signOut;
}

/**
 * Re-reads who is signed in and reloads the page if it is no longer this tab's user.
 * Resolves `true` only when the same user is confirmed (and the fresh CSRF token taken);
 * act on what the page shows only then. Never null inside the provider.
 */
export function useCheckIdentity() {
  const check = useContext(IdentityCheckContext);
  if (!check) throw new Error('useCheckIdentity must be used inside <SessionProvider>');
  return check;
}

/**
 * A stable function answering whether this tab's session has been signed out. Read it at
 * the moment of acting (an unmount cleanup), not during render: it is true from the instant
 * sign-out succeeds, before the signed-out screen has rendered. Never null inside the provider.
 */
export function useSessionEnded() {
  const ended = useContext(SessionEndedContext);
  if (!ended) throw new Error('useSessionEnded must be used inside <SessionProvider>');
  return ended;
}

/**
 * `can_write := can_be_assigned || can_send_customer_email` (docs/API.md §1.8) —
 * "has at least one working capability", which is what separates a read-only
 * viewer from everyone else without naming a role.
 */
export function useCanWrite() {
  const user = useSession();
  return user.can_be_assigned || user.can_send_customer_email;
}

/**
 * The role this user holds in `product`, or null. Display only — the server
 * enforces rank in `requireProductRole` and refuses regardless of what this says.
 */
export function useProductRole(product) {
  return productRole(useSession(), product);
}

/**
 * True when the user holds at least `minRole` in `product`. Used to decide
 * whether to RENDER an affordance, never whether to permit an action:
 * SPEC.md §7.3, "hiding a button is presentation, never authorization."
 */
export function useAtLeastRole(product, minRole) {
  return atLeastRole(useSession(), product, minRole);
}
